import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from './components/Layout';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { PlaceholderScreen } from './screens/PlaceholderScreen';
import { QueueScreen } from './screens/QueueScreen';
import { GameScreen } from './screens/GameScreen';
import { VoteScreen } from './screens/VoteScreen';
import { ResultsScreen } from './screens/ResultsScreen';
import { RatingScreen } from './screens/RatingScreen';
import { RulesScreen } from './screens/RulesScreen';
import { ProfileScreen } from './screens/ProfileScreen';

// Screen constants
const SCREEN = {
    WELCOME: 'welcome',
    QUEUE: 'queue',
    GAME: 'game',
    VOTE: 'vote',
    RESULTS: 'results',
    RATING: 'rating',
    RULES: 'rules',
    PROFILE: 'profile'
};

import { LanguageProvider } from './contexts/LanguageContext';
import { SettingsModal } from './components/SettingsModal';

function AppContent() {
    const [currentScreen, setCurrentScreen] = useState(SCREEN.WELCOME);
    const [gameData, setGameData] = useState(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    useEffect(() => {
        // Initialize Telegram SDK
        if (window.Telegram?.WebApp) {
            const tg = window.Telegram.WebApp;
            tg.ready();
            tg.expand();
        }
    }, []);

    // Handle Telegram Back Button
    useEffect(() => {
        const tg = window.Telegram?.WebApp;
        if (!tg) return;

        const backButton = tg.BackButton;

        const handleBack = () => {
            // If we are in Game or Queue, maybe we should warn? 
            // For now, simpler behavior: always go back to Welcome.
            navigateTo(SCREEN.WELCOME);
        };

        if (currentScreen !== SCREEN.WELCOME) {
            backButton.show();
            backButton.onClick(handleBack);
        } else {
            backButton.hide();
        }

        return () => {
            backButton.offClick(handleBack);
        };
    }, [currentScreen]);

    const navigateTo = useCallback((screen) => {
        setCurrentScreen(screen);
    }, []);

    const handleGameComplete = useCallback((result) => {
        if (result.phase === 'voting') {
            setGameData(prev => ({ ...prev, ...result }));
            navigateTo(SCREEN.VOTE);
        } else if (result.phase === 'results') {
            setGameData(prev => ({ ...prev, ...result }));
            navigateTo(SCREEN.RESULTS);
        }
    }, [navigateTo]);

    const handleVoteComplete = useCallback((result) => {
        setGameData(prev => ({ ...prev, ...result }));
        navigateTo(SCREEN.RESULTS);
    }, [navigateTo]);

    const renderScreen = () => {
        switch (currentScreen) {
            case SCREEN.WELCOME:
                return (
                    <WelcomeScreen
                        onStartGame={() => navigateTo(SCREEN.QUEUE)}
                        onShowRating={() => navigateTo(SCREEN.RATING)}
                        onShowRules={() => navigateTo(SCREEN.RULES)}
                        onShowProfile={() => navigateTo(SCREEN.PROFILE)}
                    />
                );
            case SCREEN.QUEUE:
                return (
                    <QueueScreen
                        onMatched={(gameData) => {
                            setGameData(gameData);
                            navigateTo(SCREEN.GAME);
                        }}
                        onLeave={() => navigateTo(SCREEN.WELCOME)}
                    />
                );
            case SCREEN.GAME:
                return (
                    <GameScreen
                        gameData={gameData}
                        onComplete={handleGameComplete}
                    />
                );
            case SCREEN.VOTE:
                return (
                    <VoteScreen
                        gameData={gameData}
                        onComplete={handleVoteComplete}
                    />
                );
            case SCREEN.RESULTS:
                return (
                    <ResultsScreen
                        data={gameData}
                        onPlayAgain={() => navigateTo(SCREEN.QUEUE)}
                        onGoHome={() => navigateTo(SCREEN.WELCOME)}
                    />
                );
            case SCREEN.RATING:
                return (
                    <RatingScreen
                        onBack={() => navigateTo(SCREEN.WELCOME)}
                        onStartGame={() => navigateTo(SCREEN.QUEUE)}
                    />
                );
            case SCREEN.RULES:
                return <RulesScreen onBack={() => navigateTo(SCREEN.WELCOME)} />;
            case SCREEN.PROFILE:
                return <ProfileScreen onBack={() => navigateTo(SCREEN.WELCOME)} />;
            default:
                return <WelcomeScreen onStartGame={() => navigateTo(SCREEN.QUEUE)} />;
        }
    };

    const showHeader = currentScreen !== SCREEN.RATING && currentScreen !== SCREEN.RULES && currentScreen !== SCREEN.PROFILE;

    return (
        <Layout onSettingsClick={() => setIsSettingsOpen(true)} showHeader={showHeader}>
            {renderScreen()}
            <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
        </Layout>
    );
}

function App() {
    return (
        <LanguageProvider>
            <AppContent />
        </LanguageProvider>
    );
}

export default App;
